import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';
import bannerImg from '../images/alma_suite_bb_housephotos.jpg';

const colazioneCtx = require.context('../images/colazione', false, /\.(png|jpe?g|svg)$/);
const allImages = colazioneCtx.keys().map(k => colazioneCtx(k));

function ColazioneePage() {
    const { isEnLang } = useLanguage();
    const { ref: introRef, inView: introInView } = useInView({ triggerOnce: true, threshold: 0.1 });
    const { ref: menuRef,  inView: menuInView  } = useInView({ triggerOnce: true, threshold: 0.1 });

    useEffect(() => {
        document.title = isEnLang
            ? 'Breakfast – B&B Alma Suite Anzio | Breakfast Included Every Morning'
            : 'Colazione – B&B Alma Suite Anzio | Colazione Inclusa Ogni Mattina';
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute('content', isEnLang
            ? 'Every morning a fresh breakfast is served at B&B Alma Suite with coffee, hot drinks, pastries and seasonal products. Always included in every stay.'
            : 'Ogni mattina una colazione fresca al B&B Alma Suite con caffè, bevande calde, pasticceria e prodotti di stagione. Sempre inclusa in ogni soggiorno.'
        );
    }, [isEnLang]);

    const T = isEnLang ? {
        label: 'Breakfast',
        bannerTitle: 'Breakfast',
        bannerSub: 'Every morning starts with flavour',
        introLabel: 'Included in every stay',
        introTitle: 'A Fresh Breakfast to Start Your Day',
        introDesc: 'At Alma Suite, breakfast is included in every stay. Every morning we prepare a welcoming breakfast with fresh pastries, coffee, hot drinks and seasonal products. Warm, welcoming, and always ready for you.',
        menuTitle: 'What We Serve',
        menuSub: 'Fresh every day',
        items: [
            { icon: '☕', title: 'Coffee & Hot Drinks', desc: 'Espresso, cappuccino, tea, barley coffee.' },
            { icon: '🥐', title: 'Fresh Pastries', desc: 'Croissants and pastries served fresh every morning.' },
            { icon: '🍓', title: 'Fresh Fruit', desc: 'Seasonal fruit, juice and natural yoghurt.' },
            { icon: '🍯', title: 'Jams & Honey', desc: 'Local products selected with care.' },
            { icon: '🥛', title: 'Milk & Cereals', desc: 'Whole or skimmed milk, mixed cereals.' },
            { icon: '🍞', title: 'Bread & Rusks', desc: 'Bread, rusks and jam.' },
        ],
        galleryTitle: 'Our Breakfasts',
        gallerySub: 'A moment of warmth every morning',
        ctaTitle: 'Book Your Stay with Breakfast',
        ctaSub: 'Breakfast is always included — no extras, no surprises',
        ctaBook: 'Book via WhatsApp',
        ctaEmail: 'Send an Email',
    } : {
        label: 'Colazione',
        bannerTitle: 'Colazione',
        bannerSub: 'Ogni mattina inizia con sapore',
        introLabel: 'Inclusa in ogni soggiorno',
        introTitle: 'Una Colazione Fresca per Iniziare la Giornata',
        introDesc: "All'Alma Suite la colazione è inclusa in ogni soggiorno. Ogni mattina prepariamo una colazione accogliente con pasticceria fresca, caffè, bevande calde e prodotti di stagione. Calda, accogliente e sempre pronta per te.",
        menuTitle: 'Cosa Serviamo',
        menuSub: 'Fresco ogni giorno',
        items: [
            { icon: '☕', title: 'Caffè e Bevande Calde', desc: 'Espresso, cappuccino, tè, caffè d\'orzo.' },
            { icon: '🥐', title: 'Pasticceria Fresca', desc: 'Cornetti e pasticcini freschi ogni mattina.' },
            { icon: '🍓', title: 'Frutta Fresca', desc: 'Frutta di stagione, succo e yogurt naturale.' },
            { icon: '🍯', title: 'Marmellate e Miele', desc: 'Prodotti locali selezionati con cura.' },
            { icon: '🥛', title: 'Latte e Cereali', desc: 'Latte intero o scremato, cereali misti.' },
            { icon: '🍞', title: 'Pane e Fette Biscottate', desc: 'Pane, fette biscottate e marmellata.' },
        ],
        galleryTitle: 'Le Nostre Colazioni',
        gallerySub: 'Un momento di calore ogni mattina',
        ctaTitle: 'Prenota con Colazione Inclusa',
        ctaSub: 'La colazione è sempre inclusa — nessun extra, nessuna sorpresa',
        ctaBook: 'Prenota su WhatsApp',
        ctaEmail: 'Scrivici via Email',
    };

    return (
        <div className="inner-page">

            <div className="inner-banner" style={{ backgroundImage: `url(${bannerImg})` }}>
                <div className="inner-banner-overlay">
                    <div className="inner-banner-text">
                        <span className="inner-banner-label">{T.label}</span>
                        <h1>{T.bannerTitle}</h1>
                        <p>{T.bannerSub}</p>
                    </div>
                </div>
            </div>

            <section className="inner-intro">
                <div className="l-768">
                    <motion.div
                        ref={introRef}
                        initial={{ opacity: 0, y: 40 }}
                        animate={introInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8 }}
                    >
                        <span className="section-label">{T.introLabel}</span>
                        <h2>{T.introTitle}</h2>
                        <p>{T.introDesc}</p>
                    </motion.div>
                </div>
            </section>

            <section className="inner-features" style={{ background: 'var(--color-bg-alt)' }}>
                <motion.div
                    ref={menuRef}
                    className="inner-features-inner l-1260"
                    initial={{ opacity: 0, y: 40 }}
                    animate={menuInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }}
                >
                    <h2>{T.menuTitle}</h2>
                    <p className="inner-features-sub">{T.menuSub}</p>
                    <div className="inner-features-grid">
                        {T.items.map((item, i) => (
                            <div className="inner-feature-item" key={i}>
                                <span className="inner-feature-icon">{item.icon}</span>
                                <h3>{item.title}</h3>
                                <p>{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </section>

            <section className="inner-gallery">
                <div className="inner-gallery-title l-1260">
                    <h2>{T.galleryTitle}</h2>
                    <p>{T.gallerySub}</p>
                </div>
                <div className="colazione-gallery-grid l-1260">
                    {allImages.slice(0, 2).map((src, i) => (
                        <img
                            key={i}
                            src={src}
                            alt={isEnLang
                                ? `Breakfast B&B Alma Suite Anzio ${i + 1}`
                                : `Colazione B&B Alma Suite Anzio ${i + 1}`}
                            loading="lazy"
                        />
                    ))}
                </div>
            </section>

            <section className="inner-cta">
                <h2>{T.ctaTitle}</h2>
                <p>{T.ctaSub}</p>
                <div className="suite-cta-buttons">
                    <a
                        href={`https://wa.me/+393928729907?text=${encodeURIComponent("Salve, vorrei prenotare al B&B Alma Suite. www.almasuitebb.it")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary"
                    >{T.ctaBook}</a>
                    <a href="mailto:almasuitebb@gmail.com" className="btn-outline-white">{T.ctaEmail}</a>
                </div>
            </section>
        </div>
    );
}

export default ColazioneePage;
